import { type UseQueryResult, useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/lib/api'

/*
 * A linha do paciente em claro: nome de exibição e status. Fica fora de
 * `api.ts` porque a trilha mora na árvore de rotas, que o portal também
 * baixa, e `api.ts` puxa a cifra junto.
 */

export const pacienteSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  status: z.enum(['lead', 'active', 'paused', 'discharged']),
})
export type Paciente = z.infer<typeof pacienteSchema>

export function pacienteKey(patientId: string): readonly unknown[] {
  return ['paciente', patientId]
}

export function usePaciente(patientId: string): UseQueryResult<Paciente, Error> {
  return useQuery({
    queryKey: pacienteKey(patientId),
    enabled: patientId !== '',
    queryFn: ({ signal }) => api(`/api/patients/${patientId}`, { schema: pacienteSchema, signal }),
  })
}

/** Só o nome, da mesma query: trilha, título e preparo leem uma cópia só. */
export function usePatientName(patientId: string): UseQueryResult<string, Error> {
  return useQuery({
    queryKey: pacienteKey(patientId),
    enabled: patientId !== '',
    queryFn: ({ signal }) => api(`/api/patients/${patientId}`, { schema: pacienteSchema, signal }),
    select: (paciente) => paciente.displayName,
  })
}
